import React from "react";
import { Cpu, LineChart, Shield, Atom } from "lucide-react";

const expertiseAreas = [
  {
    icon: Cpu,
    title: "Quantum Computing Advisory",
    description:
      "End-to-end guidance on quantum computing adoption. From identifying high-impact use cases to benchmarking algorithms on real quantum hardware, we help organizations navigate the quantum landscape with clarity and purpose.",
    tags: ["QAOA", "VQE", "Quantum ML", "Optimization", "NISQ"],
  },
  {
    icon: LineChart,
    title: "Quantitative Finance",
    description:
      "Next-generation portfolio optimization, credit scoring, and risk modeling powered by quantum-enhanced algorithms. We combine classical quantitative methods with quantum computing to unlock performance frontiers.",
    tags: ["Portfolio Optimization", "Credit Scoring", "Index Tracking", "Risk Modeling"],
  },
  {
    icon: Atom,
    title: "Quantum Machine Learning",
    description:
      "Research-driven quantum machine learning solutions for classification, semi-supervised learning, and kernel methods. From drug discovery to financial prediction, we design quantum-native ML pipelines.",
    tags: ["QML", "Kernel Methods", "Graph Neural Networks", "Semi-Supervised Learning"],
  },
  {
    icon: Shield,
    title: "Quantum Strategy & Education",
    description:
      "Workshops, keynotes, and strategic roadmaps for C-suite executives and technical teams. Author of two books on quantum computing in finance, with a track record of speaking at global conferences.",
    tags: ["Workshops", "Keynotes", "Strategy", "Training"],
  },
];

export default function ExpertiseSection() {
  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="py-24 md:py-32"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        {/* Section label */}
        <span className="font-mono text-xs uppercase tracking-[0.25em] text-[#34D399]/80 mb-4 block">
          // Areas of Expertise
        </span>

        <h2
          data-testid="expertise-heading"
          className="text-3xl md:text-5xl font-semibold tracking-tight text-[#F0FDF4] mb-4"
        >
          Where Data Meets Physics
        </h2>
        <p className="text-base md:text-lg text-[#94A3B8] max-w-2xl mb-16 leading-relaxed">
          Deep expertise in quantum computing and quantitative finance,
          delivering advisory that turns frontier research into measurable results.
        </p>

        {/* Cards grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {expertiseAreas.map((area, index) => (
            <div
              key={area.title}
              data-testid={`expertise-card-${index}`}
              className="glass-card rounded-2xl p-8 group hover:-translate-y-1 transition-transform duration-300"
            >
              {/* Icon */}
              <div className="w-12 h-12 rounded-xl bg-[#34D399]/10 border border-[#34D399]/20 flex items-center justify-center mb-6 group-hover:bg-[#34D399]/20 transition-colors">
                <area.icon size={24} className="text-[#34D399]" />
              </div>

              <h3 className="text-xl font-semibold text-[#F0FDF4] mb-3">
                {area.title}
              </h3>

              <p className="text-[#94A3B8] text-sm leading-relaxed mb-6">
                {area.description}
              </p>

              {/* Tags */}
              <div className="flex flex-wrap gap-2">
                {area.tags.map((tag) => (
                  <span
                    key={tag}
                    className="tech-badge bg-[#34D399]/10 text-[#34D399] border border-[#34D399]/20 text-xs px-3 py-1 rounded-full font-mono"
                  >
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
